import { add, dirTo, dist, scale, type Vec2 } from './vec'
import { pointInInflated, type Aabb, type Slot, type Unit } from './types'

// Movement discipline: per-unit steering is O(1) per tick. NO per-frame global
// or per-unit A* pathfinding. A unit walks straight at its target and, if a step
// would drive it inside a building box, it is pushed back out along the axis of
// least penetration (a cheap slide). Cost per unit is bounded by the number of
// nearby obstacles and a fixed pass/sample cap, which stay small and fixed.
//
// Player placement can pack buildings tightly and adjacently, so the model is
// hardened past the spike's "separated, convex, fixed-dt" precondition: the
// collision resolve iterates (a push out of one box gets a chance to clear an
// adjacent one it lands in) and the step is swept-sampled (a fast unit is much
// less likely to tunnel a thin gap between two adjacent boxes). This is
// bounded-effort hardening, NOT a proof: a fixed pass cap and a fixed sample
// count keep the work O(1) — never a search — so pathological packing (a point
// wedged among more overlapping boxes than the pass cap, or a step long enough
// to skip past the fixed samples) can still leave a small residual overlap.
// Strengthening those bounds is deferred performance debt; the cheap bound is
// the deliberate trade.
//
// Engine-free and data-oriented: nothing here imports the render engine.

/** Reach tolerance: a unit is "at" its target within this many world units. */
export const ARRIVE_EPS = 1.0

// Collision-resolve passes: pushing a point out of one box can drop it inside an
// adjacent box, so the resolve repeats until no box overlaps or this cap is hit.
// Bounded, not a search — a small constant covers realistic packing.
const RESOLVE_PASSES = 4

// Swept-step samples: interior points checked along a step so a fast unit can't
// jump across a thin obstacle between two adjacent boxes without the endpoint
// registering a penetration. Fixed count keeps the check O(1) per unit per tick.
const SWEEP_SAMPLES = 3

/**
 * Push a proposed position out of any building box it overlaps, along the
 * smallest-penetration axis, repeating up to RESOLVE_PASSES times so a push that
 * lands inside an adjacent box gets cleared too. Bounded effort, not a guarantee:
 * a point overlapping more boxes than the pass cap (dense/concave packing) can
 * return still slightly inside one. O(#obstacles x passes) — both small, fixed.
 */
export function resolveCollisions(
  pos: Vec2,
  radius: number,
  obstacles: Aabb[],
): Vec2 {
  let { x, y } = pos
  for (let pass = 0; pass < RESOLVE_PASSES; pass++) {
    let pushed = false
    for (const box of obstacles) {
      if (!pointInInflated({ x, y }, box, radius)) continue
      // Distance to push out on each side; choose the minimum.
      const pushLeft = x - (box.min.x - radius) // move to min.x - radius
      const pushRight = box.max.x + radius - x // move to max.x + radius
      const pushUp = y - (box.min.y - radius)
      const pushDown = box.max.y + radius - y
      const minPush = Math.min(pushLeft, pushRight, pushUp, pushDown)
      if (minPush === pushLeft) x = box.min.x - radius
      else if (minPush === pushRight) x = box.max.x + radius
      else if (minPush === pushUp) y = box.min.y - radius
      else y = box.max.y + radius
      pushed = true
    }
    if (!pushed) break // settled: no box overlaps this pass
  }
  return { x, y }
}

/**
 * True when a fixed few interior points along from→to fall inside a box, so a
 * step that starts and ends clear but crosses a thin obstacle (tunneling) is
 * usually caught. Un-inflated point test on a fixed sample count — it ignores
 * the unit radius and can miss a crossing that threads between samples or only
 * clips a corner, so it reduces tunneling rather than eliminating it. Fires only
 * on an actual interior crossing, never on legal edge-sliding. Bounded, O(1).
 */
function stepTunnels(from: Vec2, to: Vec2, obstacles: Aabb[]): boolean {
  const dx = to.x - from.x
  const dy = to.y - from.y
  for (let s = 1; s < SWEEP_SAMPLES; s++) {
    const t = s / SWEEP_SAMPLES
    const px = from.x + dx * t
    const py = from.y + dy * t
    for (const box of obstacles) {
      if (
        px > box.min.x &&
        px < box.max.x &&
        py > box.min.y &&
        py < box.max.y
      ) {
        return true
      }
    }
  }
  return false
}

// Fixed deflection fan (radians) tried in order when the straight path is
// blocked. Rotating in ONE consistent direction by an increasing angle makes a
// unit commit to rounding an obstacle (wall-following) instead of oscillating
// back and forth in front of it. Bounded candidate set, no A*.
const DEFLECTIONS = [0, 0.35, 0.7, 1.05, 1.4, 1.75, 2.1, 2.45, 2.8, 3.14]

function rotate(x: number, y: number, a: number): { x: number; y: number } {
  const c = Math.cos(a)
  const s = Math.sin(a)
  return { x: x * c - y * s, y: x * s + y * c }
}

/**
 * Advance one unit toward its target for dt seconds, then resolve collisions.
 * Returns true when the unit has arrived (within ARRIVE_EPS) after this step.
 *
 * The unit walks straight at its target; if that step would clip a building it
 * tries a small fixed fan of deflected headings (bounded work) and takes the
 * first whose endpoint stays clear AND whose path does not tunnel a thin
 * obstacle, so it rounds the obstacle rather than jumping through a packed gap.
 * If every candidate is blocked — e.g. boxed into a concave pocket — it falls
 * back to the collision-resolved straight step: the resolve is bounded-effort,
 * so this favours a non-penetrating move over stalling but does not itself
 * guarantee zero overlap (see resolveCollisions) and, being the last resort, is
 * not swept for tunneling. A unit in a deep concave pocket may make little net
 * progress on a given tick; it does not wedge permanently inside a wall.
 */
export function stepUnit(unit: Unit, dt: number, obstacles: Aabb[]): boolean {
  if (unit.target === null) return true
  const remaining = dist(unit.pos, unit.target)
  if (remaining <= ARRIVE_EPS) {
    unit.pos = { ...unit.target }
    return true
  }
  const stepLen = Math.min(unit.speed * dt, remaining)
  const dir = dirTo(unit.pos, unit.target)

  for (const a of DEFLECTIONS) {
    const d = rotate(dir.x, dir.y, a)
    const proposed = add(unit.pos, scale(d, stepLen))
    const resolved = resolveCollisions(proposed, unit.radius, obstacles)
    if (
      dist(resolved, proposed) <= 1e-6 &&
      !stepTunnels(unit.pos, proposed, obstacles)
    ) {
      unit.pos = resolved // clean heading — no obstacle on the path
      return dist(unit.pos, unit.target) <= ARRIVE_EPS
    }
  }

  // Fully boxed in this tick: take the non-penetrating straight step anyway.
  const proposed = add(unit.pos, scale(dir, stepLen))
  unit.pos = resolveCollisions(proposed, unit.radius, obstacles)
  return dist(unit.pos, unit.target) <= ARRIVE_EPS
}

/**
 * Naive access-slot queuing: a unit claims a free slot on the target building
 * and steers to it; while all slots are taken it waits (holds position). This
 * is the "square waits for a free slot rather than overlapping" behavior —
 * deliberately simple, not a scheduler.
 *
 * Returns the claimed slot index, or -1 if the unit must wait this tick.
 */
// Accepts any object with a `slots` array, so callers whose building type
// differs (spike Building, core SimBuilding) can both queue at access slots.
export function claimSlot(building: { slots: Slot[] }, unitId: number): number {
  // Already holding one?
  for (let i = 0; i < building.slots.length; i++) {
    if (building.slots[i].occupant === unitId) return i
  }
  for (let i = 0; i < building.slots.length; i++) {
    if (building.slots[i].occupant === null) {
      building.slots[i].occupant = unitId
      return i
    }
  }
  return -1
}

export function releaseSlot(building: { slots: Slot[] }, unitId: number): void {
  for (const slot of building.slots) {
    if (slot.occupant === unitId) slot.occupant = null
  }
}
